// The layout's geometry on the keycap grid: the width and height a layout needs, as the
// customizer and the colour, legend and layout editors size their SVG (19.05 mm per keycap
// unit) and the layout editor sizes the CNC content and picks the packaging (20.1 mm), and
// where each keycap's shadow sits, as the customizer's StaticKeycapShadows draws it, and the
// rotation transform of a turned keycap.

export const calculateLayoutWidth = (layout, oneKeycapUnitInMM = 19.05) => {
  let width = 0; // Initialize x position for keycaps

  layout.forEach((row) => {
    let rowWidth = 0; // Initialize width for the current row
    let maxXOffset = 0; // Track the largest xOffset in the row

    row.forEach((keycap, index) => {
      rowWidth += keycap.width * oneKeycapUnitInMM;

      // Check xOffset only for the last keycap in the row
      if (index === row.length - 1 && keycap.xOffset) {
        maxXOffset = Math.max(maxXOffset, keycap.xOffset);
      }
    });

    // Add the largest xOffset to the row width
    rowWidth += maxXOffset * oneKeycapUnitInMM;

    // Update the overall width if the current row is wider
    if (rowWidth > width) {
      width = rowWidth;
    }
  });

  return width;
}; // this function calculates the needed width for the svg (this enables having a dynamic svg width, based on the layout it will display)

export const calculateLayoutHeight = (layout, oneKeycapUnitInMM = 19.05) => {
  let height = 0; // Initialize y position for keycaps

  layout.forEach((row, rowIndex) => {
    height += oneKeycapUnitInMM; // For every row, 19.05/20.1 will be added to the svg's height

    row.forEach((keycap) => {
      if (keycap.isSpacer) {
        height += keycap.height * oneKeycapUnitInMM; // If the object is a spacer, its height value will be added to the svg's height
      }
    });

    // If this is the last row, find the largest yOffset value
    if (rowIndex === layout.length - 1) {
      let maxYOffset = 0;
      row.forEach((keycap) => {
        if (keycap.yOffset && keycap.yOffset > maxYOffset) {
          maxYOffset = keycap.yOffset * oneKeycapUnitInMM;
        }
      });
      height += maxYOffset; // Add the largest yOffset value to the height
    }
  });

  return height;
}; // this function calculates the needed height for the svg (this enables having a dynamic svg height, based on the layout it will display)

export const getKeycapGridPositions = (keycapData) => {
  const positions = [];
  let totalVerticalSpacing = 0; // the height of the spacers in the rows above, in mm

  keycapData.forEach((row, rowIndex) => {
    let xPosForKeycap = 0;
    const yPosForRow = rowIndex * 19.05 + totalVerticalSpacing;

    row.forEach((keycap, keycapIndex) => {
      if (keycap.isSpacer) {
        xPosForKeycap += keycap.width * 19.05; // a spacer moves the rest of its row right
        totalVerticalSpacing += keycap.height * 19.05; // and the rows below it down
        return;
      }

      const xOffset = keycap.xOffset ? keycap.xOffset * 19.05 : 0;
      const yOffset = keycap.yOffset ? keycap.yOffset * 19.05 : 0;

      positions.push({
        keycap,
        rowIndex,
        keycapIndex,
        x: xPosForKeycap + xOffset,
        y: yPosForRow + yOffset,
      });

      xPosForKeycap += keycap.isIsoEnter ? 28.575 : keycap.width * 19.05; // the ISO Enter's shadow is 1.5u wide; an offset moves only the keycap it is on, not the ones after it
    });
  });

  return positions;
}; // the top-left corner of every keycap's shadow on the 19.05 mm grid, row by row, in the order the keycaps are drawn (spacers only move the others). The unit is fixed: LayoutEditScreen's CNC content walks the same grid at 20.1 mm and can take a unit parameter when it adopts this function (the ISO Enter advance is then 1.5 × unit, which as a double differs from 28.575)

export const keycapRotationTransform = (keycap, oneKeycapUnitInMM = 19.05) =>
  `rotate(${keycap.rotation || 0}, ${
    keycap.rotationCenterX ? keycap.rotationCenterX * oneKeycapUnitInMM : 0
  }, ${
    keycap.rotationCenterY ? keycap.rotationCenterY * oneKeycapUnitInMM : 0
  })`; // the SVG transform that turns a keycap by its rotation (degrees) around its rotation centre (keycap units from the layout's origin; the origin without one), as the customizer's shadows, base layer and stroke draw it, and the layout editor's CNC content at 20.1 mm
